import {
  ProjectChat,
  projectRunFlagsForCreate,
  ProjectRunOptionsSelector,
  useCreateAndOpenProject,
  useCreateWorkChatProject,
  useWorkspaceSnapshot,
  type ObjectWorkspaceWorkbenchContext,
  type ObjectWorkspaceWorkTabDescriptor,
  type ProjectChatProps,
  type ProvisionalSubmitOutcome,
  type SubmitProjectTurnArgs,
} from '@heidi/agents';
import type { ProjectRunFlags } from '@heidi/agents/contracts';
import {
  setOpenArtifactAtom,
  useCreateProjectGuard,
  type ProjectArtifactSink,
} from '@heidi/agents/data';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';

import { useLatestRef } from '@/hooks/useLatestRef';

import {
  linkedContextForObject,
  normalizedObjectWorkspaceTabTitle,
  objectWorkspaceComposerDraftStorageKey,
  objectWorkspaceSnapshot,
} from './object-workspace-chat-utils';

const messages = defineMessages({
  composerPlaceholder: {
    defaultMessage: 'How can I help with {name}?',
    id: 'UL9CBT/DRd',
    description: 'Composer placeholder in a patient or encounter object workspace',
  },
});

export interface ObjectWorkspaceChatTab extends ObjectWorkspaceWorkTabDescriptor {
  projectId?: string;
  prefilledPrompt?: string;
  autoSubmitPrompt?: string;
  isDraftEmpty: boolean;
  hasQueuedTurns: boolean;
  isOpeningTurnInFlight: boolean;
  isTurnInFlight: boolean;
  workItemId?: string;
  anchorKey?: string;
  sessionId?: string;
}

export interface ObjectWorkspaceChatTabLifecycle {
  isDraftEmpty?: boolean;
  hasQueuedTurns?: boolean;
  isOpeningTurnInFlight?: boolean;
  isTurnInFlight?: boolean;
}

interface ObjectWorkspaceProjectChatTabProps {
  tab: ObjectWorkspaceChatTab;
  object: ObjectWorkspaceWorkbenchContext['object'];
  isActive: boolean;
  showRunOptions: boolean;
  onBindProject: (tabId: string, projectId: string) => boolean;
  onLifecycleChange: (tabId: string, lifecycle: ObjectWorkspaceChatTabLifecycle) => void;
  onSubmissionError: (tabId: string) => void;
  onSubmissionStart: (tabId: string, prompt: string) => void;
}

export function ObjectWorkspaceProjectChatTab({
  tab,
  object,
  isActive,
  showRunOptions,
  onBindProject,
  onLifecycleChange,
  onSubmissionError,
  onSubmissionStart,
}: ObjectWorkspaceProjectChatTabProps): JSX.Element {
  const intl = useIntl();
  const setOpenArtifact = useSetAtom(setOpenArtifactAtom);
  const createGuard = useCreateProjectGuard();
  const [runFlags, setRunFlags] = useState<ProjectRunFlags>({});
  const [optimisticFirstTurnText, setOptimisticFirstTurnText] = useState<string | null>(
    null
  );
  const [refusedAutoSubmitPrompt, setRefusedAutoSubmitPrompt] = useState<string>();
  const linked = useMemo(() => linkedContextForObject(object), [object]);
  const canvasWorkspace = useWorkspaceSnapshot();
  const workspace = useMemo(
    () => objectWorkspaceSnapshot(canvasWorkspace, object, tab.sessionId),
    [canvasWorkspace, object, tab.sessionId]
  );
  const tabIdRef = useLatestRef(tab.id);
  const onBindProjectRef = useLatestRef(onBindProject);
  const onLifecycleChangeRef = useLatestRef(onLifecycleChange);
  const onSubmissionErrorRef = useLatestRef(onSubmissionError);
  const onSubmissionStartRef = useLatestRef(onSubmissionStart);
  const handledAutoSubmitRef = useRef<string>();
  const artifactSink = useMemo<ProjectArtifactSink>(
    () => ({
      onOpenArtifact: setOpenArtifact,
      onOpenArtifactSilent: () => undefined,
    }),
    [setOpenArtifact]
  );

  const handleSeededProjectCreated = useCallback(
    (projectId: string): false => {
      flushSync(() => {
        onBindProjectRef.current(tabIdRef.current, projectId);
      });
      // The object workspace owns the mounted chat and URL. The create hook has
      // already seeded the opening turn, so suppress its normal route handoff.
      return false;
    },
    [onBindProjectRef, tabIdRef]
  );
  const handleSeededCreateError = useCallback(() => {
    setOptimisticFirstTurnText(null);
    onSubmissionErrorRef.current(tabIdRef.current);
  }, [onSubmissionErrorRef, tabIdRef]);
  const { createAndOpenProject, isSubmitting: isSeededCreateSubmitting } =
    useCreateAndOpenProject({
      onProjectCreated: handleSeededProjectCreated,
      onCreateError: handleSeededCreateError,
      guard: createGuard,
    });
  const { createWorkChatProject, isSubmitting: isInPlaceCreateSubmitting } =
    useCreateWorkChatProject({ guard: createGuard });
  const isSubmitting = isSeededCreateSubmitting || isInPlaceCreateSubmitting;

  useEffect(() => {
    if (tab.projectId) setOptimisticFirstTurnText(null);
  }, [tab.projectId]);

  useEffect(() => {
    onLifecycleChangeRef.current(tab.id, { isOpeningTurnInFlight: isSubmitting });
  }, [isSubmitting, onLifecycleChangeRef, tab.id]);
  useEffect(
    () => () => {
      onLifecycleChangeRef.current(tabIdRef.current, { isOpeningTurnInFlight: false });
    },
    [onLifecycleChangeRef, tabIdRef]
  );

  const handleComposerDraftEmptyChange = useCallback(
    (empty: boolean) => {
      onLifecycleChangeRef.current(tabIdRef.current, { isDraftEmpty: empty });
    },
    [onLifecycleChangeRef, tabIdRef]
  );
  const handleHasQueuedTurnsChange = useCallback(
    (hasQueuedTurns: boolean) => {
      onLifecycleChangeRef.current(tabIdRef.current, { hasQueuedTurns });
    },
    [onLifecycleChangeRef, tabIdRef]
  );
  const handleTurnInFlightChange = useCallback(
    (isTurnInFlight: boolean) => {
      onLifecycleChangeRef.current(tabIdRef.current, { isTurnInFlight });
    },
    [onLifecycleChangeRef, tabIdRef]
  );

  const createArgs = useCallback(
    (args: SubmitProjectTurnArgs, title: string) => ({
      prompt: args.text,
      ...(args.displayText ? { displayPrompt: args.displayText } : {}),
      title,
      ...(args.files?.length ? { files: args.files } : {}),
      ...(args.attachment_ids?.length ? { attachment_ids: args.attachment_ids } : {}),
      ...(args.optimisticAttachments?.length
        ? { optimisticAttachments: args.optimisticAttachments }
        : {}),
      linked,
      sessionIdOverride: tab.sessionId,
      runFlags: projectRunFlagsForCreate(runFlags),
      ...(args.computerUseSkill ? { computerUseSkill: args.computerUseSkill } : {}),
      ...(args.approvalMode ? { approvalMode: args.approvalMode } : {}),
      analytics: {
        work_mode: 'work',
        has_slash_command: args.text.trimStart().startsWith('/'),
      },
    }),
    [linked, runFlags, tab.sessionId]
  );

  useEffect(() => {
    const prompt = tab.autoSubmitPrompt;
    if (!prompt || handledAutoSubmitRef.current === prompt || tab.projectId) return;
    handledAutoSubmitRef.current = prompt;
    const title = normalizedObjectWorkspaceTabTitle(prompt, tab.title);
    setOptimisticFirstTurnText(title);
    onSubmissionStartRef.current(tab.id, title);
    if (!createAndOpenProject(createArgs({ text: prompt }, title))) {
      setOptimisticFirstTurnText(null);
      // Another create holds the guard; hand the prompt back rather than drop it.
      setRefusedAutoSubmitPrompt(prompt);
    }
  }, [createAndOpenProject, createArgs, onSubmissionStartRef, tab]);

  const handleProvisionalSubmit = useCallback(
    async (args: SubmitProjectTurnArgs): Promise<ProvisionalSubmitOutcome> => {
      const preview = normalizedObjectWorkspaceTabTitle(
        args.displayText ?? args.text,
        tab.title
      );
      setOptimisticFirstTurnText((current) => current ?? preview);
      onSubmissionStartRef.current(tab.id, preview);

      let handle;
      try {
        handle = await createWorkChatProject(createArgs(args, preview));
      } catch (error) {
        setOptimisticFirstTurnText(null);
        onSubmissionErrorRef.current(tab.id);
        throw error;
      }
      if (!handle) {
        setOptimisticFirstTurnText(null);
        return 'refused';
      }

      let bound = false;
      try {
        flushSync(() => {
          bound = onBindProjectRef.current(tab.id, handle.created.project_id);
        });
        return bound ? 'bound' : 'refused';
      } finally {
        handle.releaseGuard();
      }
    },
    [
      createArgs,
      createWorkChatProject,
      onBindProjectRef,
      onSubmissionErrorRef,
      onSubmissionStartRef,
      tab,
    ]
  );

  const composerSessionAppearance = useMemo(
    () => ({
      kind: 'default' as const,
      idlePlaceholder: intl.formatMessage(messages.composerPlaceholder, {
        name: object.name,
      }),
    }),
    [intl, object.name]
  );
  const sharedChatProps = {
    projectTitle: tab.title,
    composerPrefill: tab.prefilledPrompt ?? refusedAutoSubmitPrompt,
    provisionalFirstTurnText: optimisticFirstTurnText,
    provisionalIsSubmitting: isSubmitting,
    analyticsSurface: tab.projectId ? undefined : ('work_home' as const),
    desktopTargetRequirement: 'computer-use-only' as const,
    composerSessionAppearance,
    composerToolbarLeading:
      !tab.projectId && showRunOptions ? (
        <ProjectRunOptionsSelector
          value={runFlags}
          onChange={setRunFlags}
          disabled={isSubmitting}
        />
      ) : null,
    composerAutoFocus: isActive,
    onComposerDraftEmptyChange: handleComposerDraftEmptyChange,
    onHasQueuedTurnsChange: handleHasQueuedTurnsChange,
    onTurnInFlightChange: handleTurnInFlightChange,
    composerDraftStorageKey: objectWorkspaceComposerDraftStorageKey(object, tab.id),
    artifactSink,
    workspace,
  };
  const chatProps: ProjectChatProps = tab.projectId
    ? { ...sharedChatProps, projectId: tab.projectId }
    : {
        ...sharedChatProps,
        projectId: undefined,
        onProvisionalSubmit: handleProvisionalSubmit,
      };

  return <ProjectChat {...chatProps} />;
}
